import posthog from 'posthog-js'

// Everything up to applyAnalyticsConsent, plus applyConsent at the bottom, is
// kept in sync with website/src/lib/analytics.ts — both apps share one PostHog
// project and the cookies below. Identity and the internal-user flag are
// platform-only.

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST

// Set by the platform when an admin signs in. Lives on the shared root domain,
// so staff stay untracked on the public website too.
const INTERNAL_USER_COOKIE = 'sna_internal'

let identity: { id: string; role: string } | undefined

export const analyticsEnabled = Boolean(posthogKey)

/**
 * Domain shared by the website (najomnaagentura.sk) and the platform
 * (app.najomnaagentura.sk), so consent, the PostHog visitor id and the
 * internal-user flag carry over between them. Undefined on localhost, where a
 * domain attribute would make browsers drop the cookie.
 */
export const sharedCookieDomain = (): string | undefined => {
  const host = window.location.hostname
  if (!host.includes('.')) return undefined
  return host.replace(/^(www|app)\./, '')
}

export const isInternalUser = () =>
  document.cookie.split('; ').includes(`${INTERNAL_USER_COOKIE}=1`)

export function initAnalytics(app: 'website' | 'platform') {
  if (!posthogKey) return

  posthog.init(posthogKey, {
    api_host: posthogHost,
    // Nothing is sent or stored until the visitor accepts analytics cookies
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,
    capture_pageview: 'history_change',
    before_send: (event) => {
      if (event) event.properties.app = app
      return event
    }
  })
}

export function applyAnalyticsConsent(accepted: boolean) {
  if (!posthogKey) return

  if (accepted && !isInternalUser()) {
    posthog.opt_in_capturing()
    if (identity) posthog.identify(identity.id, { role: identity.role })
  } else {
    posthog.opt_out_capturing()
  }
}

/**
 * Flags this browser as staff on the shared domain and stops tracking. Never
 * cleared on logout: an admin's device stays untracked on both apps.
 */
export function markInternalUser() {
  const domain = sharedCookieDomain()
  document.cookie = [
    `${INTERNAL_USER_COOKIE}=1`,
    'path=/',
    'max-age=31536000',
    'SameSite=Lax',
    domain && `domain=${domain}`,
    window.location.protocol === 'https:' && 'Secure'
  ]
    .filter(Boolean)
    .join('; ')

  if (posthogKey) posthog.opt_out_capturing()
}

/**
 * Links events to the signed-in user. Only the internal id and role are sent —
 * no PII leaves the app. Remembered so that a consent given later still
 * identifies the session.
 */
export function identifyUser(id: string, role: string) {
  identity = { id, role }
  if (posthogKey && posthog.has_opted_in_capturing()) {
    posthog.identify(id, { role })
  }
}

export function resetAnalytics() {
  identity = undefined
  if (posthogKey) posthog.reset()
}

type GoogleConsent = 'granted' | 'denied'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const googleConsent = (granted: boolean): GoogleConsent => (granted ? 'granted' : 'denied')

/**
 * Forwards the cookie banner's choice to PostHog and to GTM (Google Consent
 * Mode v2). The denied defaults are set in index.html before GTM loads.
 * `cookie_consent_update` lets GTM tags without built-in consent checks
 * (e.g. Meta Pixel) trigger on the visitor's choice.
 */
export function applyConsent(categories: string[]) {
  const analytics = categories.includes('analytics')
  const marketing = categories.includes('marketing')

  applyAnalyticsConsent(analytics)

  window.gtag?.('consent', 'update', {
    analytics_storage: googleConsent(analytics),
    ad_storage: googleConsent(marketing),
    ad_user_data: googleConsent(marketing),
    ad_personalization: googleConsent(marketing)
  })
  window.dataLayer?.push({
    event: 'cookie_consent_update',
    consent_analytics: analytics,
    consent_marketing: marketing
  })
}
